import { createSelector, MemoizedSelector, select } from '@ngrx/store';

import { safeSelect, StateSelector } from '@fcom/core/selectors/selector-utils';
import {
  Category,
  FinnairCheckInEligibility,
  FinnairItineraryItemFlight,
  FinnairOrder,
  FinnairPassengerItem,
  FinnairServiceBoundItem,
  FinnairServiceItem,
} from '@fcom/dapi/api/models';
import { DapiErrorStatus, IdAndHash } from '@fcom/dapi';
import { BookingAppState } from '@fcom/common/interfaces/booking';
import { cloneDeep } from '@fcom/core/utils';
import { cartDataSelector } from '@fcom/booking/store/selectors';

import {
  CategoryStatusMap,
  CommonBookingFeatureState,
  CommonBookingState,
  OrderPartStatus,
  OrderState,
  OrderStatus,
  PassengerNameRecord,
  QuantityForFragmentAndPassenger,
  UpdatingOrderPart,
} from '../store.interface';
import { mapServicesToBookingSummary } from '../utils/services.utils';
import { BookingSummaryService } from '../../interfaces';
import { combineServices, isBoundBasedCategory } from '../../modules/ancillaries/utils';
import { isFlight } from '../../utils/flight.utils';
import { getChangeableBoundIdsFromOrder } from '../../utils/order.utils';
import { selectCommonBookingState } from './feature.selector';

const _orderState = createSelector(selectCommonBookingState, (state: CommonBookingState) => state?.order);
export const orderStatusSelector = createSelector(_orderState, (_os) => _os?.orderStatus);
const _orderStatus = orderStatusSelector;
const _orderResponseErrorStatus = createSelector(_orderState, (_os) => _os?.orderResponseErrorStatus);
export const orderDataSelector = createSelector(_orderState, (_os) => _os?.orderData);
const _hasOrderData = createSelector(_orderState, (_os) => !!_os?.orderData);

/**
 * A single location-code → city-name lookup pooled from both the cart and the order (either may be absent).
 * Booking-flow and post-booking screens share it: cart and order carry the same `locations` map shape and a
 * code resolves to the same city in both, so the two are merged without conflict handling.
 */
export const cityNameByLocationCodeSelector = createSelector(
  cartDataSelector,
  orderDataSelector,
  (cart, order): Record<string, string> =>
    [cart?.locations, order?.locations].reduce(
      (cityNames, locations) => {
        Object.entries(locations ?? {}).forEach(([code, location]) => {
          if (location.cityName) {
            cityNames[code] = location.cityName;
          }
        });
        return cityNames;
      },
      {} as Record<string, string>
    )
);

const _orderDataPrimaryPassenger = createSelector(
  orderDataSelector,
  (_od) => _od?.passengers.find((p) => p.id === _od.primaryTravelerId) || ({} as FinnairPassengerItem)
);
const _getOrderPassengers = createSelector(orderDataSelector, (_od) => _od?.passengers.map((pax) => pax.id));
export const orderPassengersSelector = createSelector(
  orderDataSelector,
  (order): FinnairPassengerItem[] => order?.passengers ?? []
);
const _getCheckInEligibilities = createSelector(orderDataSelector, (_od) => _od?.eligibilities?.checkIn || []);
const _orderFetchInformation = createSelector(_orderState, (_os) => _os?.orderFetchInformation);
export const pnrSelector = createSelector(_orderState, (_os) => _os?.PNR);
const _categoryPartStatus = (category: Category) =>
  createSelector(_orderState, (_os: OrderState) => _os?.isUpdating?.[category] ?? OrderPartStatus.OK);
const _updatingOrderPart = () => createSelector(_orderState, (_os: OrderState) => _os?.isUpdating);
export const categoryStatusMapSelector = createSelector(_orderState, (_os: OrderState) => {
  const orderPart = _os?.isUpdating;
  if (!orderPart) {
    return {} as CategoryStatusMap;
  }
  return Object.keys(orderPart).reduce((acc, categoryName) => {
    acc[categoryName as Category] = orderPart[categoryName as Category];
    return acc;
  }, {} as CategoryStatusMap);
});
const _currentServices = createSelector(orderDataSelector, (data) =>
  mapServicesToBookingSummary(data?.prices?.unpaid?.services, data?.passengers)
);
const _includedServices = (category: Category) =>
  createSelector(
    orderDataSelector,
    (_od) => _od?.services?.included?.filter((service) => service.category === category) || ([] as FinnairServiceItem[])
  );
const _getUnpaidSeatsBySegmentId = (segmentId: string) =>
  createSelector(orderDataSelector, (_od) => {
    return (
      _od?.services?.unpaid
        ?.flatMap((service) => (service.category === Category.SEAT ? service.bounds : []))
        .filter((bound) => bound.segments.some((segment) => segment.id === segmentId)) || []
    );
  });
const _includedVariantsQuantity = (variants: string[]) =>
  createSelector(orderDataSelector, (order): QuantityForFragmentAndPassenger => {
    const included = order?.services?.included ?? [];
    return included.reduce((allCategoriesQuantity, selection: FinnairServiceItem) => {
      return selection.bounds
        .flatMap((bound) =>
          (isBoundBasedCategory(selection.category) ? [bound.segments[0]] : bound.segments).map((segment) => ({
            ...segment,
            bound,
          }))
        )
        .flatMap((segment) => segment.passengers.map((passengerService) => ({ ...passengerService, segment })))
        .flatMap((passenger) =>
          passenger.services
            .filter((service) => variants.includes(service.variant))
            .map((service) => {
              const fragmentId = isBoundBasedCategory(selection.category)
                ? passenger.segment.bound.id
                : passenger.segment.id;
              return { ...service, fragmentId, passenger };
            })
        )
        .reduce((fragmentQuantity, { fragmentId, quantity, passenger }) => {
          if (!fragmentQuantity[fragmentId]) {
            fragmentQuantity[fragmentId] = {};
          }
          fragmentQuantity[fragmentId][passenger.id] = quantity + (fragmentQuantity[fragmentId][passenger.id] ?? 0);
          return fragmentQuantity;
        }, allCategoriesQuantity);
    }, {});
  });

const _includedServicesMap = createSelector(
  orderDataSelector,
  (_od) =>
    _od?.services?.included?.reduce((categories, service) => {
      categories[service.category] = cloneDeep(service);
      return categories;
    }, {}) ?? {}
);

/**
 * Number of already-purchased (order `included`) services per fragment, passenger and variant for a category.
 * Used to offset tiered pricing (already-bought items consume the earlier tier slots).
 */
const _includedServiceQuantitiesPerFragmentAndPax = (category: Category) =>
  createSelector(orderDataSelector, (order): Record<string, Record<string, Record<string, number>>> =>
    (order?.services?.included ?? [])
      .filter((service) => service.category === category)
      .reduce(
        (acc, serviceItem) => {
          serviceItem.bounds.forEach((bound) => {
            const segments = isBoundBasedCategory(category) ? [bound.segments[0]] : bound.segments;
            segments.forEach((segment) => {
              const fragmentId = isBoundBasedCategory(category) ? bound.id : segment.id;
              segment.passengers.forEach((passenger) => {
                passenger.services.forEach((service) => {
                  acc[fragmentId] = acc[fragmentId] ?? {};
                  acc[fragmentId][passenger.id] = acc[fragmentId][passenger.id] ?? {};
                  acc[fragmentId][passenger.id][service.variant] =
                    (acc[fragmentId][passenger.id][service.variant] ?? 0) + (service.quantity ?? 1);
                });
              });
            });
          });
          return acc;
        },
        {} as Record<string, Record<string, Record<string, number>>>
      )
  );

const _combinedServices = createSelector(orderDataSelector, (_od) =>
  combineServices([_od?.services?.included ?? [], _od?.services?.unpaid ?? []])
);
const _combinedServicesMap = createSelector(_combinedServices, (_services) =>
  _services.reduce((categories, service) => {
    categories[service.category] = cloneDeep(service);
    return categories;
  }, {})
);
const _orderFlights = () =>
  createSelector(
    orderDataSelector,
    (od) => od?.bounds?.reduce((all, b) => all.concat(...b.itinerary.filter(isFlight)), []) ?? []
  );
const _acceptTerms = createSelector(_orderState, (_os: OrderState) => !!_os?.acceptTerms);
const _analyticsSent = createSelector(_orderState, (_os: OrderState) => !!_os?.analyticsSent);
const _changeableBoundIds = createSelector(orderDataSelector, getChangeableBoundIdsFromOrder);

export const orderState = (): StateSelector<CommonBookingFeatureState, OrderState> => select(_orderState);
export const orderStatus = (): StateSelector<CommonBookingFeatureState, OrderStatus> => safeSelect(_orderStatus);
export const orderResponseErrorStatus = (): StateSelector<CommonBookingFeatureState, DapiErrorStatus> =>
  safeSelect(_orderResponseErrorStatus);
export const orderData = (emitUndefined = false): StateSelector<CommonBookingFeatureState, FinnairOrder> =>
  !emitUndefined ? safeSelect(orderDataSelector) : select(orderDataSelector);
export const hasOrderData = (): StateSelector<CommonBookingFeatureState, boolean> => select(_hasOrderData);
export const orderFlights = (): StateSelector<CommonBookingFeatureState, FinnairItineraryItemFlight[]> =>
  safeSelect(_orderFlights());

export const orderDataPrimaryPassenger = (): StateSelector<CommonBookingFeatureState, FinnairPassengerItem> =>
  select(_orderDataPrimaryPassenger);
export const getOrderPassengers = (): StateSelector<CommonBookingFeatureState, string[]> => select(_getOrderPassengers);
export const getCheckInEligibilities = (): StateSelector<CommonBookingFeatureState, FinnairCheckInEligibility[]> =>
  select(_getCheckInEligibilities);
export const PNR = (): StateSelector<CommonBookingFeatureState, PassengerNameRecord> => select(pnrSelector);
export const categoryPartStatus = (category: Category): StateSelector<CommonBookingFeatureState, OrderPartStatus> =>
  safeSelect(_categoryPartStatus(category));
export const updatingOrderPart = (): StateSelector<CommonBookingFeatureState, UpdatingOrderPart> =>
  select(_updatingOrderPart());
export const categoryStatusMap = (): StateSelector<CommonBookingFeatureState, CategoryStatusMap> =>
  select(categoryStatusMapSelector);
export const currentServices = (): StateSelector<BookingAppState, BookingSummaryService[]> =>
  safeSelect(_currentServices);
export const includedServices = (category: Category): StateSelector<CommonBookingFeatureState, FinnairServiceItem[]> =>
  safeSelect(_includedServices(category));
export const includedServicesMap = (): StateSelector<CommonBookingFeatureState, Record<string, FinnairServiceItem>> =>
  safeSelect(_includedServicesMap);

/**
 * Composable selector (for use inside other `createSelector` compositions) returning the number of
 * already-purchased services per fragment, passenger and variant for the given category.
 */
export const includedServiceQuantitiesPerFragmentAndPaxSelector = (
  category: Category
): MemoizedSelector<CommonBookingFeatureState, Record<string, Record<string, Record<string, number>>>> =>
  _includedServiceQuantitiesPerFragmentAndPax(category);

export const combinedServicesMap = (): StateSelector<CommonBookingFeatureState, Record<string, FinnairServiceItem>> =>
  safeSelect(_combinedServicesMap);

export const getChangeableBoundIds = (): StateSelector<CommonBookingFeatureState, string[]> =>
  select(_changeableBoundIds);

export const includedVariantsQuantity = (
  variants: string[]
): StateSelector<CommonBookingFeatureState, QuantityForFragmentAndPassenger> =>
  select(_includedVariantsQuantity(variants));

/**
 * To be used when fetching order information from the backend first time
 */
export const orderFetchInformation = (): StateSelector<CommonBookingFeatureState, IdAndHash> =>
  select(_orderFetchInformation);

export const acceptTerms = (): StateSelector<CommonBookingFeatureState, boolean> => safeSelect(_acceptTerms);

export const analyticsSent = (): StateSelector<CommonBookingFeatureState, boolean> => safeSelect(_analyticsSent);

export const getUnpaidSeatsBySegmentId = (
  segmentId: string
): StateSelector<CommonBookingFeatureState, FinnairServiceBoundItem[]> =>
  safeSelect(_getUnpaidSeatsBySegmentId(segmentId));
